import { Controller } from "stimulus"

console.log('🚀🚀🚀 UPPY CONTROLLER FILE v2.1 IS BEING LOADED 🚀🚀🚀')

// Lazy load Uppy and its styles
let Uppy, Dashboard, ImageEditor, Dropbox, GoogleDrive, Instagram, Url, Webcam
let uppyLoaded = false

async function loadUppy() {
  if (!uppyLoaded) {
    // Dynamic imports - only loaded when needed
    const [
      uppyModule,
      dashboardModule,
      imageEditorModule,
      dropboxModule,
      googleDriveModule,
      instagramModule,
      urlModule,
      webcamModule
    ] = await Promise.all([
      import('@uppy/core'),
      import('@uppy/dashboard'),
      import('@uppy/image-editor'),
      import('@uppy/dropbox'),
      import('@uppy/google-drive'),
      import('@uppy/instagram'),
      import('@uppy/url'),
      import('@uppy/webcam'),
      import('@uppy/core/css/style.min.css'),
      import('@uppy/dashboard/css/style.min.css'),
      import('@uppy/image-editor/css/style.min.css')
    ])

    Uppy = uppyModule.default
    Dashboard = dashboardModule.default
    ImageEditor = imageEditorModule.default
    Dropbox = dropboxModule.default
    GoogleDrive = googleDriveModule.default
    Instagram = instagramModule.default
    Url = urlModule.default
    Webcam = webcamModule.default
    uppyLoaded = true
  }

  return { Uppy, Dashboard, ImageEditor, Dropbox, GoogleDrive, Instagram, Url, Webcam }
}

class UppyImageEditorController extends Controller {
  static targets = ["preview", "input", "removeBtn", "trigger"]
  static values = {
    aspectRatio: Number,
    width: Number,
    height: Number,
    defaultImage: String
  }

  connect() {
    console.log('🔵 UPPY CONTROLLER v2.1 - CONNECT CALLED', this.element.id)
    try {
      this.uppyReady = false
      this.hasUnsavedChanges = false
      this.updatePreview()
      this.setupBeforeUnloadHandler()
    } catch (error) {
      console.error('Error in Uppy controller connect:', error)
    }
  }

  disconnect() {
    if (this.uppy) {
      this.uppy.close()
    }
    this.removeBeforeUnloadHandler()
  }

  async initializeUppy() {
    if (this.uppyReady) return

    console.log('Loading Uppy libraries...')
    await loadUppy()

    const aspectRatio = this.aspectRatioValue || (this.widthValue / this.heightValue)
    console.log('Initializing Uppy with aspect ratio:', aspectRatio)

    this.uppy = new Uppy({
      restrictions: {
        maxFileSize: 5 * 1024 * 1024, // 5MB
        maxNumberOfFiles: 1,
        allowedFileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml']
      },
      autoProceed: false,
      // Don't actually upload - we'll handle file via form submission
      onBeforeUpload: (files) => {
        console.log('Preventing Uppy upload, will use form submission instead')
        // Process files and close dashboard
        Object.values(files).forEach(file => {
          this.processUploadedFile(file)
        })
        // Close dashboard
        const dashboard = this.uppy.getPlugin('Dashboard')
        if (dashboard) {
          dashboard.closeModal()
        }
        // Cancel the upload
        return false
      }
    })
    .use(Dashboard, {
      inline: false,
      trigger: null,  // Don't use trigger, we'll manually open it
      closeAfterFinish: true,
      showProgressDetails: true,
      proudlyDisplayPoweredByUppy: false,
      note: `Image should be ${this.widthValue}x${this.heightValue}px. Max file size: 5MB (JPG, JPEG, PNG, SVG)`
    })
    .use(Dropbox, {
      companionUrl: 'https://companion.uppy.io'
    })
    .use(GoogleDrive, {
      companionUrl: 'https://companion.uppy.io'
    })
    .use(Instagram, {
      companionUrl: 'https://companion.uppy.io'
    })
    .use(Url, {
      companionUrl: 'https://companion.uppy.io'
    })
    .use(Webcam, {
      modes: ['picture']
    })
    .use(ImageEditor, {
      quality: 0.9,
      cropperOptions: {
        viewMode: 1,
        background: false,
        autoCropArea: 1,
        responsive: true,
        aspectRatio: aspectRatio,
        initialAspectRatio: aspectRatio,
        // Ensure crop box is movable and resizable but maintains aspect ratio
        dragMode: 'move',
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
        checkCrossOrigin: false,
        // Strict aspect ratio enforcement
        strict: true,
        guides: true,
        center: true,
        highlight: true,
        autoCrop: true
      },
      actions: {
        revert: true,
        rotate: true,
        granularRotate: true,
        flip: true,
        zoomIn: true,
        zoomOut: true,
        cropSquare: false,
        cropWidescreen: false,
        cropWidescreenVertical: false
      }
    })

    // Handle file added - open editor immediately, check dimensions in background
    this.uppy.on('file-added', (file) => {
      console.log('🔵 v2.1 File added:', file.name, file)

      // Open image editor immediately for better UX
      // Use a longer delay to ensure dashboard and all plugins are fully rendered
      setTimeout(() => {
        const dashboard = this.uppy.getPlugin('Dashboard')
        const imageEditor = this.uppy.getPlugin('ImageEditor')

        console.log('🔵 v2.1 Attempting to open image editor...')
        console.log('🔵 v2.1 Dashboard:', dashboard)
        console.log('🔵 v2.1 ImageEditor:', imageEditor)
        console.log('🔵 v2.1 Dashboard.openFileEditor exists?', dashboard && typeof dashboard.openFileEditor)
        console.log('🔵 v2.1 ImageEditor.selectFile exists?', imageEditor && typeof imageEditor.selectFile)

        if (imageEditor && dashboard) {
          console.log('🔵 v2.1 Opening image editor for file:', file.id)
          // Try using the dashboard's openFileEditor method
          if (dashboard.openFileEditor) {
            console.log('🔵 v2.1 Calling dashboard.openFileEditor()')
            dashboard.openFileEditor(file)
          } else if (imageEditor.selectFile) {
            console.log('🔵 v2.1 Calling imageEditor.selectFile()')
            imageEditor.selectFile(file)
          } else {
            console.error('🔴 v2.1 No method available to open image editor')
          }
        } else {
          console.error('🔴 v2.1 ImageEditor or Dashboard plugin not available')
        }
      }, 300)

      // Check image dimensions in background and warn if too small
      this.checkImageDimensions(file).then(({ width, height, isValid, message }) => {
        if (!isValid) {
          // Show warning but allow user to continue editing
          alert(
            `⚠️ Image Quality Warning\n\n${message}\n\nRecommended dimensions: ${this.widthValue}x${this.heightValue}px\nYour image: ${width}x${height}px\n\nThe image may appear pixelated or blurry after cropping.`
          )
        }
      }).catch(error => {
        console.error('Error checking image dimensions:', error)
      })
    })

    // Handle when image editor saves the edited image
    this.uppy.on('file-editor:complete', async (updatedFile) => {
      console.log('🟡 v2.1 Image editor complete, edited file:', updatedFile)

      // Force resize to exact dimensions after cropping
      const targetWidth = this.widthValue
      const targetHeight = this.heightValue

      console.log('🟡 v2.1 Resizing image to exact dimensions:', targetWidth, 'x', targetHeight)

      try {
        const resizedFile = await this.resizeImage(updatedFile.data, targetWidth, targetHeight, updatedFile.name)
        console.log('🟡 v2.1 Image resized successfully, new size:', resizedFile.size, 'Type:', resizedFile.constructor.name)
        console.log('🟡 v2.1 Is File?', resizedFile instanceof File, 'Is Blob?', resizedFile instanceof Blob)

        // Update the file data with resized version
        this.uppy.setFileState(updatedFile.id, {
          data: resizedFile,
          size: resizedFile.size
        })
        console.log('🟡 v2.1 File state updated with resized image')

        // Process the file immediately
        console.log('🟡 v2.1 Processing file after edit...')
        const finalFile = this.uppy.getFile(updatedFile.id)
        console.log('🟡 v2.1 Final file from Uppy:', finalFile)
        this.processUploadedFile(finalFile)

        // Close the dashboard - no need for user to click upload
        const dashboard = this.uppy.getPlugin('Dashboard')
        if (dashboard) {
          console.log('🟡 v2.1 Closing dashboard after successful edit')
          dashboard.closeModal()
        }

        // Clear the file from Uppy (it's now in the form input)
        this.uppy.removeFile(updatedFile.id)
      } catch (error) {
        console.error('🔴 v2.1 Error resizing image:', error)
        console.error('🔴 v2.1 Error stack:', error.stack)
      }
    })

    // Note: We don't use the 'complete' event because we intercept in onBeforeUpload
    // and prevent actual upload. The file is assigned to the form input instead.

    this.uppyReady = true
    console.log('Uppy initialized and ready')
  }

  async openUppy(event) {
    // Prevent default button behavior
    if (event) {
      event.preventDefault()
    }

    // Initialize if not ready
    if (!this.uppyReady) {
      console.log('Initializing Uppy on first click...')
      await this.initializeUppy()
      // Give plugins a moment to fully register
      await new Promise(resolve => setTimeout(resolve, 50))
    }

    // Manually open the dashboard
    console.log('Opening Uppy dashboard...', 'this.uppy:', this.uppy)
    const dashboard = this.uppy.getPlugin('Dashboard')
    console.log('Dashboard plugin:', dashboard)
    if (dashboard) {
      dashboard.openModal()
      console.log('Dashboard opened')
    } else {
      console.error('Dashboard plugin not found')
    }
  }

  convertAndAssignFile(uppyFile) {
    console.log('Converting Uppy file to browser File:', uppyFile)
    // Uppy file.data is already a File/Blob object
    this.processUploadedFile(uppyFile)
  }

  processUploadedFile(file) {
    console.log('🟢 v2.1 Processing uploaded file:', file)

    // Get the file data (already edited/cropped by Uppy)
    const fileData = file.data
    console.log('🟢 v2.1 File data type:', fileData.constructor.name, 'Size:', fileData.size, 'Is File?', fileData instanceof File, 'Is Blob?', fileData instanceof Blob)

    try {
      const dataTransfer = new DataTransfer()
      console.log('🟢 v2.1 Created DataTransfer, about to add file...')
      dataTransfer.items.add(fileData)
      console.log('🟢 v2.1 Successfully added file to DataTransfer')

      // Update the hidden file input
      this.inputTarget.files = dataTransfer.files
      console.log('🟢 v2.1 Updated input target with file:', this.inputTarget.files[0])

      // Mark as having unsaved changes
      this.hasUnsavedChanges = true
      console.log('🟢 v2.1 Marked as having unsaved changes')

      // Trigger change event
      const event = new Event('change', { bubbles: true })
      this.inputTarget.dispatchEvent(event)
      console.log('🟢 v2.1 Dispatched change event on input')

      // Update preview
      const previewUrl = URL.createObjectURL(fileData)
      this.updatePreviewWithFile(previewUrl)
      console.log('🟢 v2.1 Updated preview with URL:', previewUrl)

      console.log('✅ v2.1 File processing completed successfully')
    } catch (error) {
      console.error('🔴 v2.1 ERROR in processUploadedFile:', error)
      console.error('🔴 v2.1 Error stack:', error.stack)
    }
  }

  updatePreviewWithFile(url) {
    console.log('Updating preview with:', url)
    if (this.hasPreviewTarget) {
      this.previewTarget.src = url
      this.previewTarget.style.display = 'block'

      if (this.hasRemoveBtnTarget) {
        this.removeBtnTarget.style.display = 'inline-flex'
      }
    }
  }

  updatePreview() {
    if (this.hasPreviewTarget) {
      // Show current image or default placeholder
      if (this.previewTarget.src && this.previewTarget.src !== window.location.href) {
        this.previewTarget.style.display = 'block'
        if (this.hasRemoveBtnTarget) {
          this.removeBtnTarget.style.display = 'inline-flex'
        }
      } else if (this.defaultImageValue) {
        this.previewTarget.src = this.defaultImageValue
        this.previewTarget.style.display = 'block'
      }
    }
  }

  remove(event) {
    event.preventDefault()

    if (confirm('Are you sure you want to remove this image?')) {
      // Clear the file input
      this.inputTarget.value = ''

      // Hide preview or show default
      if (this.hasPreviewTarget) {
        if (this.defaultImageValue) {
          this.previewTarget.src = this.defaultImageValue
        } else {
          this.previewTarget.src = ''
          this.previewTarget.style.display = 'none'
        }
      }

      // Hide remove button
      if (this.hasRemoveBtnTarget) {
        this.removeBtnTarget.style.display = 'none'
      }

      // Trigger change event to update form state
      const changeEvent = new Event('change', { bubbles: true })
      this.inputTarget.dispatchEvent(changeEvent)
    }
  }

  // Helper method to resize image to exact dimensions
  async resizeImage(blob, targetWidth, targetHeight, originalFileName) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(blob)

      img.onload = () => {
        URL.revokeObjectURL(url)

        // Create canvas with target dimensions
        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight
        const ctx = canvas.getContext('2d')

        // Draw image scaled to exact dimensions
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight)

        // Convert canvas to blob, then to File
        canvas.toBlob((resultBlob) => {
          if (resultBlob) {
            // Convert Blob to File for DataTransfer compatibility
            const file = new File([resultBlob], originalFileName, {
              type: blob.type || 'image/jpeg',
              lastModified: Date.now()
            })
            resolve(file)
          } else {
            reject(new Error('Failed to create blob from canvas'))
          }
        }, blob.type || 'image/jpeg', 0.9)
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('Failed to load image'))
      }

      img.src = url
    })
  }

  // Check if image dimensions meet minimum requirements
  async checkImageDimensions(file) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file.data)

      img.onload = () => {
        URL.revokeObjectURL(url)

        const width = img.naturalWidth
        const height = img.naturalHeight
        const targetWidth = this.widthValue
        const targetHeight = this.heightValue

        // Check if image is smaller than target dimensions
        let isValid = true
        let message = ''

        if (width < targetWidth || height < targetHeight) {
          isValid = false
          if (width < targetWidth && height < targetHeight) {
            message = 'Your image is too small in both width and height.'
          } else if (width < targetWidth) {
            message = 'Your image width is too small.'
          } else {
            message = 'Your image height is too small.'
          }
        }

        resolve({ width, height, isValid, message })
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('Failed to load image for dimension check'))
      }

      img.src = url
    })
  }

  // Setup beforeunload handler to warn about unsaved changes
  setupBeforeUnloadHandler() {
    // Handler for page navigation/refresh/close
    // Note: Modern browsers don't allow custom messages, they show their own generic warning
    this.beforeUnloadHandler = (event) => {
      if (this.hasUnsavedChanges) {
        event.preventDefault()
        event.returnValue = '' // Required for Chrome
        return '' // Required for older browsers
      }
    }

    window.addEventListener('beforeunload', this.beforeUnloadHandler)

    // Handler for Bootstrap collapse events (accordion tabs)
    this.collapseShowHandler = (event) => {
      if (!this.hasUnsavedChanges) return

      // Check if we're in one of the accordion sections
      const currentForm = this.element.closest('.accordion-collapse')
      if (!currentForm) return

      // If trying to collapse/show a different section
      if (event.target !== currentForm) {
        const confirmed = confirm('You have uploaded images that have not been saved.\n\nIf you switch sections without saving, your uploaded images will be lost.\n\nDo you want to continue without saving?')

        if (!confirmed) {
          event.preventDefault()
          event.stopPropagation()
          return false
        } else {
          // User confirmed, clear the flag
          this.hasUnsavedChanges = false
        }
      }
    }

    // Listen for Bootstrap collapse show events on all accordion items
    document.querySelectorAll('.accordion-collapse').forEach(accordion => {
      accordion.addEventListener('show.bs.collapse', this.collapseShowHandler)
    })

    // Handler for in-page link clicks (navigation links, etc.)
    this.clickInterceptHandler = (event) => {
      if (!this.hasUnsavedChanges) return

      // Check if click is on a link (but not accordion buttons or submit buttons)
      const target = event.target.closest('a')
      if (!target) return

      // Skip if it's an accordion toggle
      if (target.hasAttribute('data-bs-toggle')) return

      // Skip if clicking the save button
      const form = this.element.closest('form')
      if (form && target.type === 'submit' && form.contains(target)) {
        this.hasUnsavedChanges = false
        return
      }

      // Show confirmation dialog for other links
      const confirmed = confirm('You have uploaded images that have not been saved.\n\nIf you navigate away, your uploaded images will be lost.\n\nDo you want to continue without saving?')

      if (!confirmed) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
      } else {
        this.hasUnsavedChanges = false
      }
    }

    // Add click interceptor to document (capture phase to catch it early)
    document.addEventListener('click', this.clickInterceptHandler, true)

    // Intercept form submission to clear the flag
    const form = this.element.closest('form')
    if (form) {
      this.formSubmitHandler = () => {
        this.hasUnsavedChanges = false
      }
      form.addEventListener('submit', this.formSubmitHandler)
    }
  }

  removeBeforeUnloadHandler() {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    }

    if (this.collapseShowHandler) {
      document.querySelectorAll('.accordion-collapse').forEach(accordion => {
        accordion.removeEventListener('show.bs.collapse', this.collapseShowHandler)
      })
    }

    if (this.clickInterceptHandler) {
      document.removeEventListener('click', this.clickInterceptHandler, true)
    }

    if (this.formSubmitHandler) {
      const form = this.element.closest('form')
      if (form) {
        form.removeEventListener('submit', this.formSubmitHandler)
      }
    }
  }
}

console.log('!!! Exporting UppyImageEditorController class !!!', UppyImageEditorController)
export default UppyImageEditorController
